// ChoosePlan.js
// Shown right after a successful registration so the merchant can pick a
// subscription tier before moving on to payment. Reuses the same plan data
// + card layout as the Landing Page pricing section (see
// src/constants/plans.js and src/components/PricingPlans.jsx).
//
// Advanced/Premium no longer link straight to a static Stripe Payment Link.
// That had no way to tell Stripe "use this merchant's existing Stripe
// Customer", so Stripe minted a brand new Customer on every checkout —
// different from account_user.stripe_customer_id created at registration —
// which made the webhook's `WHERE stripe_customer_id = session.customer`
// lookup fail silently. Instead, the backend now creates the Checkout
// Session itself (passing the existing customer explicitly) and hands back
// its URL to redirect to.
import React, { useState } from "react";
import { Button } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import PricingPlans from "../components/PricingPlans";
import { SUBSCRIPTION_PLANS } from "../constants/plans";
import logoImage from "../images/logo-black.png";
import API_BASE from "../config/api";
import "../importData/Homepage.css";

const CONTACT_PHONE_DISPLAY = "+1 914 469 2987";
const CONTACT_PHONE_HREF = "tel:+19144692987";

// Only Advanced lets the merchant choose a screen count before checkout —
// sent as `quantity` to /onboarding/plan/select. Premium is fixed at 1.
const MIN_QUANTITY = 1;
const QUANTITY_PLAN_IDS = ["advanced"];

const ChoosePlan = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Registration hands the customerId along via router state; fall back to
  // localStorage in case the user lands here directly (e.g. a refresh).
  const customerId =
    location.state?.customerId || localStorage.getItem("customer_id") || null;

  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const [error, setError] = useState("");
  // { advanced: 1, premium: 1 } — one independent counter per plan, so
  // adjusting Advanced's count doesn't affect Premium's and vice versa.
  const [quantities, setQuantities] = useState(
    () => Object.fromEntries(QUANTITY_PLAN_IDS.map((id) => [id, MIN_QUANTITY]))
  );

  const adjustQuantity = (planId, delta) => {
    setQuantities((prev) => ({
      ...prev,
      [planId]: Math.max(MIN_QUANTITY, prev[planId] + delta),
    }));
  };

  const handleSelectPlan = async (plan, quantity) => {
    setError("");

    if (!customerId) {
      // Log which source was missing so this is diagnosable from the
      // console instead of guessing — a fresh registration should always
      // have set both location.state.customerId and localStorage
      // customer_id in Register.js's handleRegistrationSuccess.
      console.error(
        "ChoosePlan: no customerId available.",
        {
          fromRouteState: location.state?.customerId || null,
          fromLocalStorage: localStorage.getItem("customer_id"),
        }
      );
      setError(
        "We couldn't find your account. Please try registering again, or contact support if the problem continues."
      );
      return;
    }

    setLoadingPlanId(plan.id);
    try {
      const res = await fetch(`${API_BASE}/onboarding/plan/select`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          quantity ? { customerId, plan: plan.id, quantity } : { customerId, plan: plan.id }
        ),
      });
      const data = await res.json();

      if (!res.ok || !data.success || !data.url) {
        throw new Error(data.message || "Could not start checkout. Please try again.");
      }

      // Full browser navigation to Stripe's hosted Checkout page — not
      // react-router, since this is leaving the app entirely.
      window.location.href = data.url;
    } catch (err) {
      setError(err.message || "Could not start checkout. Please try again.");
      setLoadingPlanId(null);
    }
  };

  // For a merchant who can't pay right now — nothing downstream currently
  // gates on having an active subscription, so it's safe to just move on to
  // the app. `replace` so the back button doesn't return here from /home.
  const handleSkip = () => {
    navigate("/home", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background">
      <section className="py-20 md:py-32 bg-background relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(59,130,246,0.05),transparent_50%)]"></div>

        <div className="container px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16 animate-fade-in">
            <img
              src={logoImage}
              alt="Logo"
              className="h-10 mx-auto mb-6"
              style={{ height: "40px" }}
            />
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Choose Your Plan
            </h2>
            <p className="text-lg text-muted-foreground">
              You're almost there! Pick the plan that fits your business
              needs to continue setting up your account.
            </p>
            {error && (
              <p className="text-sm mt-4" style={{ color: "#dc2626" }}>
                {error}
              </p>
            )}
          </div>

          <PricingPlans
            plans={SUBSCRIPTION_PLANS}
            renderAction={(plan) => {
              // Basic has no self-serve checkout yet — send them to sales.
              if (plan.id === "basic") {
                return (
                  <Button
                    href={CONTACT_PHONE_HREF}
                    className={plan.buttonClassName}
                    size="lg"
                  >
                    Contact {CONTACT_PHONE_DISPLAY}
                  </Button>
                );
              }

              // Advanced / Premium: ask the backend to create a Checkout
              // Session (reusing the existing Stripe Customer), then follow
              // the URL it returns.
              const isLoading = loadingPlanId === plan.id;
              const hasQuantitySelector = QUANTITY_PLAN_IDS.includes(plan.id);
              const planQuantity = quantities[plan.id] ?? MIN_QUANTITY;

              return (
                <>
                  {hasQuantitySelector && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "16px",
                        marginBottom: "12px",
                      }}
                    >
                      <Button
                        shape="circle"
                        disabled={loadingPlanId !== null || planQuantity <= MIN_QUANTITY}
                        onClick={() => adjustQuantity(plan.id, -1)}
                        aria-label="Decrease screen count"
                      >
                        −
                      </Button>
                      <span style={{ minWidth: "90px", textAlign: "center" }}>
                        {planQuantity} {planQuantity === 1 ? "screen" : "screens"}
                      </span>
                      <Button
                        shape="circle"
                        disabled={loadingPlanId !== null}
                        onClick={() => adjustQuantity(plan.id, 1)}
                        aria-label="Increase screen count"
                      >
                        +
                      </Button>
                    </div>
                  )}
                  <Button
                    className={plan.buttonClassName}
                    size="lg"
                    disabled={loadingPlanId !== null}
                    onClick={() =>
                      handleSelectPlan(plan, hasQuantitySelector ? planQuantity : undefined)
                    }
                  >
                    {isLoading ? "Redirecting…" : "Select Plan"}
                  </Button>
                </>
              );
            }}
          />

          <div className="text-center mt-10">
            <button
              type="button"
              onClick={handleSkip}
              className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground transition-colors bg-transparent border-0 cursor-pointer"
            >
              Can't pay right now? Skip for now
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ChoosePlan;
